// src/components/ProfileDetails.js
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import EditProfileForm from './forms/EditProfileForm';
import { getAdminProfile } from '../services/AdminAuthService';
import config from '../config/config'; // Import config directly 
import defaultProfile from '../assets/img/default-profile.jpg';
import '../assets/css/Profile.css'

const ProfileDetails = ({ user, onEdit }) => {
  const location = useLocation();
  const [adminProfile, setAdminProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  // Detect if this is the admin area
  const isAdmin = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdmin) {
      setLoading(true);
      getAdminProfile()
        .then((res) => {
          if (res && res.success && res.admin) {
            setAdminProfile(res.admin);
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [isAdmin]);

  // Use adminProfile if in admin, otherwise use user prop
  const profile = isAdmin && adminProfile ? adminProfile : user;

  const [isEditing, setIsEditing] = useState(false);

  const handleEditProfile = () => {
    setIsEditing(true);
  };

  if (isEditing) {
    return <EditProfileForm user={user} onCancel={() => setIsEditing(false)} />;
  }

  if (loading) {
    return <div className="text-center text-white">Loading profile...</div>;
  }

  if (!profile) {
    return <div className="alert alert-danger">Profile not found.</div>;
  }

  // Parse user data safely
  const userName = profile?.name || 'User';
  const userEmail = profile?.email || 'No email provided';
  const userType = profile?.user_type || 'No user type';
  const userLocation = profile?.location || 'No location provided';
  
  // Use config.UPLOADS instead of hardcoded API URL or API.UPLOADS
  const profileImage = profile?.profile_image 
    ? `${config.IMAGE_URL}/profiles/${profile.profile_image}`
    : defaultProfile;
  
  // Business details (if applicable)
  const isBusiness = userType === 'event_promoter';
  const businessName = profile?.business?.business_name || 'No business name';
  const businessContact = profile?.business?.business_contact_name || 'No contact name';
  const businessType = profile?.business?.business_type || 'No business type';
  const businessLocation = profile?.business?.business_location || 'No business location';

  return (
    <section id="profile-details" className="pt-5 mt-4">
      <div className="container-xl">
        <div className="row">
          <div className="col-md-10 offset-md-1">
            <div className="card bg_grey border-0 shadow">
              <div className="card-body p-4">
                <div className="row align-items-center">
                  <div className="col-md-3 text-center mb-3 mb-md-0">
                    <img 
                      src={profileImage}
                      alt={userName} 
                      className="img-fluid rounded-circle mb-3 profile-image"
                      style={{ 
                        width: '180px', 
                        height: '180px',
                        objectFit: 'cover',
                        border: '4px solid #ff3636',
                        boxShadow: '0 0 20px rgba(255, 54, 54, 0.3)'
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = defaultProfile;
                      }}
                    />
                    <h4 className="text-white mb-0">{userName}</h4>
                    <p className="text-light mb-2">{userType}</p>
                    <button 
                      className="button btn-sm mt-2" 
                      onClick={onEdit}
                    >
                      <i className="fa fa-pencil me-1"></i> Edit Profile
                    </button>
                  </div>
                  
                  <div className="col-md-9">
                    <div className="card bg-dark text-white mb-4">
                      <div className="card-header bg-transparent border-bottom border-danger">
                        <h4 className="mb-0 text-danger">Profile Information</h4>
                      </div>
                      <div className="card-body">
                        <div className="row mb-3">
                          <div className="col-sm-4">
                            <h6 className="mb-0">Full Name</h6>
                          </div>
                          <div className="col-sm-8 text-light">
                            {userName}
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-sm-4">
                            <h6 className="mb-0">Email</h6>
                          </div>
                          <div className="col-sm-8 text-light">
                            {userEmail}
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-sm-4">
                            <h6 className="mb-0">User Type</h6>
                          </div>
                          <div className="col-sm-8 text-light">
                            {userType}
                          </div>
                        </div>
                        <div className="row mb-3">
                          <div className="col-sm-4">
                            <h6 className="mb-0">Location</h6>
                          </div>
                          <div className="col-sm-8 text-light">
                            {userLocation}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Business information for event promoters */}
                    {isBusiness && (
                      <div className="card bg-dark text-white">
                        <div className="card-header bg-transparent border-bottom border-danger">
                          <h4 className="mb-0 text-danger">Business Information</h4>
                        </div>
                        <div className="card-body">
                          <div className="row mb-3">
                            <div className="col-sm-4">
                              <h6 className="mb-0">Business Name</h6>
                            </div>
                            <div className="col-sm-8 text-light">
                              {businessName}
                            </div>
                          </div>
                          <div className="row mb-3">
                            <div className="col-sm-4">
                              <h6 className="mb-0">Contact Person</h6>
                            </div>
                            <div className="col-sm-8 text-light">
                              {businessContact}
                            </div>
                          </div>
                          <div className="row mb-3">
                            <div className="col-sm-4">
                              <h6 className="mb-0">Business Type</h6>
                            </div>
                            <div className="col-sm-8 text-light">
                              {businessType}
                            </div>
                          </div>
                          <div className="row mb-3">
                            <div className="col-sm-4">
                              <h6 className="mb-0">Business Location</h6>
                            </div>
                            <div className="col-sm-8 text-light">
                              {businessLocation}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

ProfileDetails.propTypes = {
  user: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired
};

export default ProfileDetails;
